import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import JsonBlock from "./JsonBlock";
import "./RainWizard.css";

interface HistoryItem {
  _id: string;
  disputeId: string;
  status: string;
  confidence: number;
  timestamp: string;
  question: string | null;
  options: string[] | null;
  selected_option: number | null;
  selected_option_text: string | null;
}

interface HistoryResponse {
  disputes: HistoryItem[];
  pagination: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_items: number;
  };
}

interface ReasoningDetail {
  decision?: {
    finding_summary?: string;
    gpt_analysis?: string;
    perplexity_analysis?: string;
  };
  [key: string]: unknown;
}

const stripHtml = (html: string): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const HistoryPage: React.FC = () => {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, ReasoningDetail>>({});

  const load = useCallback(async (targetPage: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.get<HistoryResponse>("/api/admin/disputes", {
        params: { type: "rain", page: targetPage, limit: 15 },
      });
      setItems(data.disputes);
      setTotalPages(data.pagination.total_pages || 1);
      setTotalItems(data.pagination.total_items || data.disputes.length);
      setPage(targetPage);
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load decisions."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  const toggle = async (item: HistoryItem) => {
    if (openId === item.disputeId) {
      setOpenId(null);
      return;
    }
    setOpenId(item.disputeId);
    if (!details[item.disputeId]) {
      try {
        const { data } = await api.get(
          `/api/admin/reasoning/${item.disputeId}`
        );
        setDetails((prev) => ({ ...prev, [item.disputeId]: data }));
      } catch {
        setDetails((prev) => ({
          ...prev,
          [item.disputeId]: { decision: { finding_summary: undefined } },
        }));
      }
    }
  };

  return (
    <div className="rw-container rw-history">
      <div className="rw-card">
        <h2 className="rw-step-title">Decision history</h2>
        <p className="rw-step-subtitle">
          All Rain decisions - yours and production ones - straight from the
          database.
          {totalItems > 0 ? ` ${totalItems} total.` : ""}
        </p>

        {error && <div className="rw-error rw-submit-error">{error}</div>}

        {isLoading ? (
          <div className="rw-history-loading">
            <span className="rw-spinner rw-spinner-sm" />
            Loading decisions...
          </div>
        ) : items.length === 0 ? (
          <p className="rw-hint">No decisions yet.</p>
        ) : (
          <ul className="rw-history-list">
            {items.map((item) => {
              const noDecision =
                item.selected_option === null || item.selected_option < 0;
              const detail = details[item.disputeId];
              const isOpen = openId === item.disputeId;
              return (
                <li key={item._id} className="rw-history-item">
                  <button
                    type="button"
                    className="rw-history-row"
                    onClick={() => toggle(item)}
                  >
                    <span
                      className={`rw-history-outcome ${
                        noDecision ? "rw-outcome-open" : "rw-outcome-decided"
                      }`}
                    >
                      {noDecision
                        ? item.selected_option_text || "Too early"
                        : item.selected_option_text ||
                          `Outcome ${(item.selected_option as number) + 1}`}
                    </span>
                    <span className="rw-history-question">
                      {item.question || "(no question)"}
                    </span>
                    <span className="rw-history-meta">
                      {Math.round((item.confidence || 0) * 100)}% &middot;{" "}
                      {new Date(item.timestamp).toLocaleString()}
                    </span>
                    <svg
                      className={`rw-history-chevron ${isOpen ? "open" : ""}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M7 10l5 5 5-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {isOpen && (
                    <div className="rw-history-detail">
                      {item.options && (
                        <div className="rw-review-block">
                          <div className="rw-review-label">Outcomes</div>
                          <ol className="rw-review-options">
                            {item.options.map((option, i) => (
                              <li
                                key={i}
                                className={
                                  i === item.selected_option
                                    ? "rw-option-selected"
                                    : undefined
                                }
                              >
                                {option}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {!detail ? (
                        <div className="rw-history-loading">
                          <span className="rw-spinner rw-spinner-xs" />
                          Loading details...
                        </div>
                      ) : (
                        <>
                          {detail.decision?.finding_summary && (
                            <div className="rw-review-block">
                              <div className="rw-review-label">Why</div>
                              <div className="rw-review-value rw-result-summary">
                                {stripHtml(detail.decision.finding_summary)}
                              </div>
                            </div>
                          )}
                          <details className="rw-details">
                            <summary>Developer view - stored record</summary>
                            <JsonBlock
                              label={`Decision ${item.disputeId}`}
                              value={detail}
                            />
                          </details>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="rw-history-pager">
            <button
              type="button"
              className="rw-secondary-btn"
              disabled={page <= 1 || isLoading}
              onClick={() => load(page - 1)}
            >
              Newer
            </button>
            <span className="rw-history-page">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="rw-secondary-btn"
              disabled={page >= totalPages || isLoading}
              onClick={() => load(page + 1)}
            >
              Older
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
